import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { Car, Clock, Phone } from 'lucide-react'

import { HAIRDRESSING_SERVICES } from '@/components/additional-services'
import { cn } from '@/shared/lib/styles/class-merge'
import { Badge } from '@/shared/ui/badge'
import { UnscheduledHairSalonTask } from '@monorepo/functions/src/types/reservations'
import { serviceTypeColors } from '@/shared/utils/serviceTypeColors'

import { getPetFromReservation } from '../model/task-utils'

interface DraggableReservationProps {
    task: UnscheduledHairSalonTask
    isSelected?: boolean
    onTaskClick?: (task: UnscheduledHairSalonTask, e: React.MouseEvent) => void
    onTaskDoubleClick?: (task: UnscheduledHairSalonTask, e: React.MouseEvent) => void
}

export function UnscheduledHairSalonTaskCard({
    task,
    isSelected = false,
    onTaskClick,
    onTaskDoubleClick,
}: DraggableReservationProps) {
    const hasDriverService = task.reservation.additionalServices.some(service => service.type === 'driver')
    const { t } = useTranslation()
    // Helper function to safely format dates
    const formatSafeDate = (dateString: string | undefined, formatStr: string) => {
        if (!dateString) return ''
        try {
            const date = new Date(dateString)
            if (isNaN(date.getTime())) return ''
            return format(date, formatStr, { locale: es })
        } catch (error) {
            console.error('Error formatting date:', error)
            return ''
        }
    }

    const pet = getPetFromReservation(task.reservation, task.petIndex)
    const petName = pet?.name ?? t('hairSalon.unknownPet', 'Mascota sin datos')
    const petBreed = pet?.breed ?? '—'

    return (
        <div
            className={cn(
                'h-full w-[300px] rounded-sm border p-2 transition-colors',
                task.reservation.type === 'hotel' ? 'border-emerald-200 bg-emerald-50' : 'border-pink-200 bg-pink-50',
                isSelected && 'ring-2 ring-blue-500',
            )}
            onClick={
                onTaskClick
                    ? e => {
                          e.stopPropagation()
                          onTaskClick(task, e)
                      }
                    : undefined
            }
            onDoubleClick={
                onTaskDoubleClick
                    ? e => {
                          e.stopPropagation()
                          onTaskDoubleClick(task, e)
                      }
                    : undefined
            }
        >
            <div className='flex h-full flex-col justify-between gap-1'>
                {/* Fila 1: Nombre del perro | Servicio (y coche si hay) */}
                <div className='flex items-center justify-between'>
                    <div className='text-sm font-bold'>{petName}</div>
                    <div className='flex items-center gap-1'>
                        {hasDriverService && <Car className='h-4 w-4 text-gray-500' />}
                        {task.service && (
                            <Badge
                                variant='outline'
                                className={cn(
                                    'whitespace-nowrap border-[1.5px] px-1.5 py-0 text-[10px]',
                                    serviceTypeColors[task.service],
                                )}
                            >
                                {t(
                                    HAIRDRESSING_SERVICES.find(service => service.value === task.service)
                                        ?.translationKey || '',
                                )}
                            </Badge>
                        )}
                    </div>
                </div>
                {/* Fila 2: Raza | Nombre del cliente */}
                <div className='flex items-center justify-between'>
                    <span className='text-sm text-gray-600'>{petBreed}</span>
                    <span className='text-sm text-gray-600'>{task.reservation.client.name}</span>
                </div>
                {/* Fila 3: Fechas (o fecha/hora) | Teléfono */}
                <div className='mt-1 flex items-start justify-between text-[11px] text-gray-600'>
                    <div className='flex flex-col gap-0.5'>
                        {task.reservation.type === 'hotel' ? (
                            <>
                                <div className='flex items-center gap-1'>
                                    <Clock className='h-3 w-3' />
                                    <span>
                                        Entrada: {formatSafeDate(task.reservation.checkInDate, "d 'de' MMM")}
                                        {task.reservation.checkInTime && ` - ${task.reservation.checkInTime}`}
                                    </span>
                                </div>
                                <div className='flex items-center gap-1'>
                                    <Clock className='h-3 w-3' />
                                    <span>
                                        Salida: {formatSafeDate(task.reservation.checkOutDate, "d 'de' MMM")}
                                        {task.reservation.checkOutTime && ` - ${task.reservation.checkOutTime}`}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <div className='flex items-center gap-1'>
                                <Clock className='h-3 w-3' />
                                <span>
                                    {formatSafeDate(task.reservation.date, "d 'de' MMM")}
                                    {task.reservation.time && ` - ${task.reservation.time}`}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className='flex items-center gap-1'>
                        <Phone className='h-3 w-3' />
                        {task.reservation.client.phone}
                    </div>
                </div>
            </div>
        </div>
    )
}
